/**
 * Search farms and food-dropoff points along a tour driving route.
 * Geometry helpers are haversine / equirectangular so they stay testable
 * without pulling Turf into Node scripts. Tour Planner uses Turf only to
 * draw the corridor polygon on the map.
 */

export const DEFAULT_FARM_RADIUS_MILES = 25;
export const MIN_FARM_RADIUS_MILES = 5;
export const MAX_FARM_RADIUS_MILES = 100;
export const FARM_CORRIDOR_SOURCE = 'farm-corridor';
export const FARM_CORRIDOR_FILL_LAYER = 'farm-corridor-fill';
export const FARM_CORRIDOR_LINE_LAYER = 'farm-corridor-line';

const MILES_PER_DEG_LAT = 69.0;
const METERS_PER_MILE = 1609.344;

export function clampFarmRadiusMiles(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return DEFAULT_FARM_RADIUS_MILES;
  return Math.min(MAX_FARM_RADIUS_MILES, Math.max(MIN_FARM_RADIUS_MILES, Math.round(n)));
}

export function haversineMiles(lat1, lng1, lat2, lng2) {
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 3958.7613 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function toLocalMiles(lat, lng, lat0) {
  const cos = Math.cos((lat0 * Math.PI) / 180);
  return {
    x: lng * MILES_PER_DEG_LAT * cos,
    y: lat * MILES_PER_DEG_LAT,
  };
}

export function pointToSegmentDistanceMiles(lat, lng, aLng, aLat, bLng, bLat) {
  const lat0 = (lat + aLat + bLat) / 3;
  const p = toLocalMiles(lat, lng, lat0);
  const a = toLocalMiles(aLat, aLng, lat0);
  const b = toLocalMiles(bLat, bLng, lat0);
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  if (len2 < 1e-12) {
    const ex = p.x - a.x;
    const ey = p.y - a.y;
    return Math.sqrt(ex * ex + ey * ey);
  }
  const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2));
  const ex = p.x - (a.x + t * dx);
  const ey = p.y - (a.y + t * dy);
  return Math.sqrt(ex * ex + ey * ey);
}

/** `coordinates` are Mapbox [lng, lat] pairs. */
export function pointToLineDistanceMiles(lat, lng, coordinates) {
  if (!Array.isArray(coordinates) || coordinates.length === 0) return Infinity;
  if (coordinates.length === 1) {
    const [lng0, lat0] = coordinates[0];
    return haversineMiles(lat, lng, lat0, lng0);
  }
  let min = Infinity;
  for (let i = 0; i < coordinates.length - 1; i += 1) {
    const a = coordinates[i];
    const b = coordinates[i + 1];
    if (!a || !b) continue;
    const d = pointToSegmentDistanceMiles(lat, lng, a[0], a[1], b[0], b[1]);
    if (d < min) min = d;
  }
  return min;
}

export function collectRouteLineCoordinates(geometries) {
  const coords = [];
  (geometries || []).forEach((geo) => {
    const type = geo?.type;
    if (type === 'LineString' && Array.isArray(geo.coordinates)) {
      geo.coordinates.forEach((pair) => {
        const lng = Number(pair?.[0]);
        const lat = Number(pair?.[1]);
        if (Number.isFinite(lng) && Number.isFinite(lat)) coords.push([lng, lat]);
      });
      return;
    }
    if (type === 'MultiLineString' && Array.isArray(geo.coordinates)) {
      geo.coordinates.forEach((line) => {
        (line || []).forEach((pair) => {
          const lng = Number(pair?.[0]);
          const lat = Number(pair?.[1]);
          if (Number.isFinite(lng) && Number.isFinite(lat)) coords.push([lng, lat]);
        });
      });
    }
  });
  return coords;
}

export function sampleRouteCoordinates(coordinates, { minSpacingMiles = 12, maxPoints = 60 } = {}) {
  if (!Array.isArray(coordinates) || coordinates.length === 0) return [];
  const sampled = [coordinates[0]];
  for (let i = 1; i < coordinates.length; i += 1) {
    const prev = sampled[sampled.length - 1];
    const cur = coordinates[i];
    const miles = haversineMiles(prev[1], prev[0], cur[1], cur[0]);
    if (miles >= minSpacingMiles) sampled.push(cur);
  }
  const last = coordinates[coordinates.length - 1];
  const lastSampled = sampled[sampled.length - 1];
  if (last && (lastSampled[0] !== last[0] || lastSampled[1] !== last[1])) {
    sampled.push(last);
  }
  if (sampled.length <= maxPoints) return sampled;
  const step = (sampled.length - 1) / (maxPoints - 1);
  const thinned = [];
  for (let i = 0; i < maxPoints; i += 1) {
    thinned.push(sampled[Math.round(i * step)]);
  }
  return thinned;
}

export function isPointWithinRouteRadius(point, lineCoords, radiusMiles) {
  const lat = Number(point?.latitude ?? point?.lat);
  const lng = Number(point?.longitude ?? point?.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  const radius = clampFarmRadiusMiles(radiusMiles);
  return pointToLineDistanceMiles(lat, lng, lineCoords) <= radius + 1e-6;
}

export function milesFromRoute(point, lineCoords) {
  const lat = Number(point?.latitude ?? point?.lat);
  const lng = Number(point?.longitude ?? point?.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  const miles = pointToLineDistanceMiles(lat, lng, lineCoords);
  return Number.isFinite(miles) ? miles : null;
}

export function isFoodBankTags(tags = {}) {
  const amenity = String(tags.amenity || '');
  const facility = String(tags.social_facility || '');
  return (
    facility === 'food_bank'
    || facility === 'food_pantry'
    || amenity === 'food_bank'
    || amenity === 'community_fridge'
    || amenity === 'food_sharing'
  );
}

export function isFarmTags(tags = {}) {
  return (
    tags.place === 'farm' ||
    tags.shop === 'farm' ||
    tags.shop === 'farm_shop' ||
    tags.landuse === 'farmyard'
  );
}

export function readHubCoords(item) {
  if (!item) return null;
  const lat = Number(
    item.latitude
    ?? item.lat
    ?? item.coords?.latitude
    ?? item.coords?.lat
    ?? item.location?.latitude
    ?? item.location?.lat
  );
  const lng = Number(
    item.longitude
    ?? item.lng
    ?? item.lon
    ?? item.coords?.longitude
    ?? item.coords?.lng
    ?? item.location?.longitude
    ?? item.location?.lng
  );
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { latitude: lat, longitude: lng };
}

export function isFoodDirectoryItem(item) {
  if (!item) return false;
  const hay = [
    item.submissionType,
    item.category,
    item.categoryLabel,
    item.locationType,
    item.kind,
  ].map((value) => String(value || '').toLowerCase()).join(' ');
  return /\bfood\b|farm|fridge|pantry|food.?bank|food.?hub|food.?sharing/.test(hay);
}

export function inferFoodHubFlags(item) {
  const type = String(item?.locationType || item?.categoryLabel || item?.kind || '').toLowerCase();
  let pickup = Boolean(item?.pickup);
  let dropoff = Boolean(item?.dropoff);
  if (!pickup && !dropoff) {
    if (/fridge|pantry|food.?bank|dropoff|sharing/.test(type)) dropoff = true;
    else if (/farm|pickup/.test(type)) pickup = true;
    else {
      pickup = true;
      dropoff = true;
    }
  }
  if (pickup && !dropoff && !/fridge|pantry|food.?bank/.test(type)) dropoff = true;
  return { pickup, dropoff };
}

export function foodHubMatchesRailTab(item, tab) {
  if (!item) return false;
  const type = String(item.locationType || item.categoryLabel || '').toLowerCase();
  if (tab === 'pickup') {
    return Boolean(item.pickup) || item.kind === 'pickup' || /farm|pickup/.test(type);
  }
  return Boolean(item.dropoff) || item.kind === 'dropoff' || /fridge|pantry|food.?bank|dropoff|sharing/.test(type);
}

export function farmDayCardParts(day) {
  const name = String(day?.name || 'Day').trim() || 'Day';
  const raw = String(day?.scheduledDate || '').trim();
  const routeCount = Array.isArray(day?.legs) ? day.legs.length : Number(day?.stopCount) || 0;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return { dow: 'DAY', num: '', month: '', name, routeCount };
  }
  const [year, month, dayNum] = raw.split('-').map(Number);
  const date = new Date(year, month - 1, dayNum);
  return {
    dow: date.toLocaleString('en-US', { weekday: 'short' }).toUpperCase(),
    num: String(dayNum),
    month: date.toLocaleString('en-US', { month: 'short' }).toUpperCase(),
    name,
    routeCount,
  };
}

/**
 * Farms can be used as dropoff locations (produce / food recovery along the
 * route). Food banks are dropoff-only.
 */
export function classifyFoodHubKind(source) {
  const tags = source?.tags && typeof source.tags === 'object' ? source.tags : source || {};
  const type = String(source?.locationType || source?.categoryLabel || source?.kind || '').toLowerCase();
  if (
    isFoodBankTags(tags)
    || source?.kind === 'dropoff'
    || (source?.dropoff && !source?.pickup)
    || /fridge|pantry|food.?bank/.test(type)
  ) {
    return 'dropoff';
  }
  return 'pickup';
}

export const FARM_OVERPASS_SELECTORS = [
  '["place"="farm"]["name"]',
  '["shop"="farm"]["name"]',
  '["shop"="farm_shop"]["name"]',
  '["landuse"="farmyard"]["name"]',
];

export const FOOD_BANK_OVERPASS_SELECTORS = [
  '["social_facility"="food_bank"]',
  '["social_facility"="food_pantry"]',
  '["amenity"="food_bank"]',
  '["amenity"="community_fridge"]',
  '["amenity"="food_sharing"]',
];

export const OVERPASS_ENDPOINTS = [
  'https://overpass-api.de/api/interpreter',
  'https://overpass.kumi.systems/api/interpreter',
];
export const OVERPASS_TIMEOUT_MS = 18000;

function aroundClause(sampledCoords, radiusMeters) {
  const pairs = sampledCoords
    .map((pair) => `${Number(pair[1]).toFixed(5)},${Number(pair[0]).toFixed(5)}`)
    .join(',');
  return `(around:${Math.round(radiusMeters)},${pairs})`;
}

export function buildAroundOverpassQuery({
  sampledCoords,
  radiusMiles = DEFAULT_FARM_RADIUS_MILES,
  includeFarms = true,
  includeFoodBanks = true,
}) {
  if (!Array.isArray(sampledCoords) || sampledCoords.length === 0) return '';
  const radius = clampFarmRadiusMiles(radiusMiles);
  const around = aroundClause(sampledCoords, radius * METERS_PER_MILE);
  const selectors = [
    ...(includeFarms ? FARM_OVERPASS_SELECTORS : []),
    ...(includeFoodBanks ? FOOD_BANK_OVERPASS_SELECTORS : []),
  ];
  if (selectors.length === 0) return '';
  const parts = [];
  selectors.forEach((sel) => {
    parts.push(`  node${sel}${around};`);
    parts.push(`  way${sel}${around};`);
  });
  return `[out:json][timeout:45];\n(\n${parts.join('\n')}\n);\nout center;`;
}

function hubId(prefix, rawId) {
  return `${prefix}:${String(rawId || '').trim()}`;
}

function fallbackFoodHubName(tags, farm, bank) {
  if (bank) {
    if (tags.amenity === 'community_fridge') return 'Community fridge';
    if (tags.amenity === 'food_sharing') return 'Food sharing point';
    if (tags.social_facility === 'food_pantry') return 'Food pantry';
    return 'Food bank';
  }
  if (farm) return 'Farm';
  return 'Food location';
}

export function overpassElementToFoodHub(element) {
  const lat = Number(element?.lat ?? element?.center?.lat);
  const lng = Number(element?.lon ?? element?.center?.lon);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  const tags = element.tags || {};
  const farm = isFarmTags(tags);
  const bank = isFoodBankTags(tags);
  if (!farm && !bank) return null;
  let name = String(tags.name || '').trim();
  if (name.length < 2) {
    if (!bank) return null;
    name = fallbackFoodHubName(tags, farm, bank);
  }
  const kind = bank ? 'dropoff' : 'pickup';
  const street = [tags['addr:housenumber'], tags['addr:street']].filter(Boolean).join(' ').trim();
  const city = tags['addr:city'] || '';
  const state = tags['addr:state'] || '';
  const address = [street, city, state].filter(Boolean).join(', ');
  const fridge = tags.amenity === 'community_fridge' || tags.amenity === 'food_sharing' || tags.social_facility === 'food_pantry';
  return {
    id: hubId('osm', `${element.type || 'node'}_${element.id}`),
    name,
    latitude: lat,
    longitude: lng,
    address,
    city,
    state,
    description: fridge ? 'Community fridge / pantry' : (bank ? 'Food bank (drop off)' : 'Farm'),
    pickup: farm || !bank,
    dropoff: true,
    kind,
    locationType: bank ? 'fridge' : 'farm',
    source: 'osm',
    website: tags.website || tags['contact:website'] || '',
    searchText: [name, address, city, state, kind, farm ? 'farm pickup' : '', bank ? 'fridge pantry food bank dropoff' : '']
      .filter(Boolean)
      .join(' ')
      .toLowerCase(),
  };
}

export function approvedLocationToFoodHub(item) {
  if (!item) return null;
  const coords = readHubCoords(item);
  if (!coords) return null;
  const { pickup, dropoff } = inferFoodHubFlags(item);
  const kind = pickup && !dropoff ? 'pickup' : (dropoff && !pickup ? 'dropoff' : classifyFoodHubKind({ ...item, pickup, dropoff }));
  const name = String(item.name || item.resourceName || 'Food location').trim();
  return {
    ...item,
    id: item.id?.startsWith?.('dir:') || item.id?.startsWith?.('osm:')
      ? item.id
      : hubId('dir', item.id),
    name,
    latitude: coords.latitude,
    longitude: coords.longitude,
    pickup,
    dropoff,
    kind,
    locationType: item.locationType || (dropoff && !pickup ? 'fridge' : 'farm'),
    source: item.source || 'directory',
    searchText: String(item.searchText || [
      name,
      item.description,
      item.city,
      item.state,
      pickup ? 'farm pickup' : '',
      dropoff ? 'fridge pantry food bank dropoff' : '',
    ].filter(Boolean).join(' ')).toLowerCase(),
  };
}

export function normalizeFoodDirectoryItem(item) {
  if (!item || !isFoodDirectoryItem(item)) return null;
  const hub = approvedLocationToFoodHub({
    ...item,
    name: item.name || item.resourceName || 'Food location',
  });
  if (hub) return hub;
  const { pickup, dropoff } = inferFoodHubFlags(item);
  const name = String(item.name || item.resourceName || 'Food location').trim();
  return {
    ...item,
    id: item.id,
    name,
    pickup,
    dropoff,
    kind: dropoff && !pickup ? 'dropoff' : 'pickup',
    locationType: item.locationType || (dropoff && !pickup ? 'fridge' : 'farm'),
    searchText: [
      name,
      item.description,
      item.city,
      item.state,
      pickup ? 'farm pickup' : '',
      dropoff ? 'fridge pantry dropoff' : '',
    ].filter(Boolean).join(' ').toLowerCase(),
  };
}

export function mergeFoodHubs(lists) {
  const seen = new Set();
  const merged = [];
  (lists || []).forEach((list) => {
    (list || []).forEach((hub) => {
      if (!hub?.id || seen.has(hub.id)) return;
      seen.add(hub.id);
      merged.push(hub);
    });
  });
  return merged;
}

export function filterHubsAlongRoute(hubs, lineCoords, radiusMiles) {
  const radius = clampFarmRadiusMiles(radiusMiles);
  return (hubs || [])
    .map((hub) => {
      const miles = milesFromRoute(hub, lineCoords);
      if (miles == null || miles > radius + 1e-6) return null;
      return { ...hub, milesFromRoute: miles };
    })
    .filter(Boolean)
    .sort((a, b) => (a.milesFromRoute ?? 99) - (b.milesFromRoute ?? 99));
}

export function hubMatchesFoodFilters(hub, { showFarms, showFoodBanks }) {
  if (!hub) return false;
  const isBank = hub.kind === 'dropoff' || (Boolean(hub.dropoff) && !hub.pickup);
  const isFarm = hub.kind === 'pickup' || Boolean(hub.pickup);
  if (showFarms) return isFarm || isBank;
  if (showFoodBanks) return isBank;
  return false;
}

export function foodStopVenueFromHub(hub, { role = 'dropoff' } = {}) {
  const foodRole = role === 'pickup' ? 'pickup' : 'dropoff';
  return {
    id: `food-${hub.id}`,
    name: hub.name || (foodRole === 'pickup' ? 'Farm pickup' : 'Food dropoff'),
    latitude: Number(hub.latitude),
    longitude: Number(hub.longitude),
    address: hub.address || '',
    fullAddress: hub.address || '',
    city: hub.city || '',
    state: hub.state || '',
    venueType: 'Food',
    isFoodLocation: true,
    isFoodDropoff: foodRole === 'dropoff',
    foodRole,
    foodHubId: hub.id,
    foodHubKind: hub.kind,
    website: hub.website || hub.url || '',
    description: hub.description || '',
  };
}

export function riderFoodMarkerId(hubId) {
  return String(hubId || '').replace(/[^a-zA-Z0-9:_-]/g, '');
}

export function riderFoodSnippetHtml(hub, { role = 'dropoff', milesFromRoute = null } = {}) {
  const foodRole = role === 'pickup' ? 'pickup' : 'dropoff';
  const title = foodRole === 'pickup' ? 'Farm pickup' : 'Food dropoff';
  const name = String(hub?.name || 'Food location').replace(/[<>]/g, '');
  const address = String(hub?.address || [hub?.city, hub?.state].filter(Boolean).join(', ') || '').replace(/[<>]/g, '');
  const miles = Number.isFinite(Number(milesFromRoute))
    ? ` · ${Number(milesFromRoute).toFixed(1)} mi off route`
    : '';
  const instruction = foodRole === 'pickup'
    ? 'Use this farm as the pickup location for this stop.'
    : 'Use this location as the food dropoff for this stop.';
  const marker = riderFoodMarkerId(hub?.id);
  return [
    `<!-- vrc-food-stop:${marker} -->`,
    `<p><strong>${title}:</strong> ${name}${miles}</p>`,
    address ? `<p>${address}</p>` : '',
    `<p>${instruction}</p>`,
    `<!-- /vrc-food-stop:${marker} -->`,
  ].filter(Boolean).join('\n');
}

export function upsertRiderFoodSnippet(existingHtml, snippet, hubId) {
  const marker = riderFoodMarkerId(hubId);
  const current = String(existingHtml || '');
  const block = String(snippet || '').trim();
  if (!block || !marker) return current;
  const re = new RegExp(`<!-- vrc-food-stop:${marker} -->[\\s\\S]*?<!-- /vrc-food-stop:${marker} -->`);
  if (re.test(current)) {
    return current.replace(re, block);
  }
  if (!current.trim()) return block;
  return `${current.trim()}\n\n${block}`;
}

export function foodStopRecordFromHub(hub, { role = 'dropoff', dayId = null } = {}) {
  return {
    id: hub.id,
    name: hub.name,
    latitude: hub.latitude,
    longitude: hub.longitude,
    address: hub.address || '',
    city: hub.city || '',
    state: hub.state || '',
    foodRole: role === 'pickup' ? 'pickup' : 'dropoff',
    kind: hub.kind,
    dayId,
    milesFromRoute: hub.milesFromRoute ?? null,
  };
}

function stopHasCoords(stop) {
  return Number.isFinite(Number(stop?.latitude)) && Number.isFinite(Number(stop?.longitude));
}

export function resolveFarmSearchDayId(tourDays, preferredId) {
  const days = Array.isArray(tourDays) ? tourDays : [];
  if (preferredId && days.some((day) => day.id === preferredId)) return preferredId;
  const withRoute = days.find((day) => (day.venues || []).filter(stopHasCoords).length >= 2);
  return withRoute?.id || days[0]?.id || null;
}

export function listTourRouteLegs(tourDays, interDayLegModes = []) {
  const legs = [];
  const days = Array.isArray(tourDays) ? tourDays : [];
  days.forEach((day, dayIndex) => {
    const venues = day?.venues || [];
    const modes = day?.legModes || [];
    for (let i = 0; i < venues.length - 1; i += 1) {
      const from = venues[i];
      const to = venues[i + 1];
      if (!stopHasCoords(from) || !stopHasCoords(to)) continue;
      legs.push({
        id: `${day.id}:${i}`,
        dayId: day.id,
        dayName: day.name || `Day ${dayIndex + 1}`,
        scheduledDate: day.scheduledDate || '',
        fromName: from.name || 'Stop',
        toName: to.name || 'Stop',
        fromId: from.id,
        toId: to.id,
        mode: modes[i] || 'drive',
        kind: 'day',
        index: i,
      });
    }
    const next = days[dayIndex + 1];
    if (!next) return;
    const last = venues[venues.length - 1];
    const firstNext = (next.venues || [])[0];
    if (!stopHasCoords(last) || !stopHasCoords(firstNext)) return;
    legs.push({
      id: `inter:${day.id}:${next.id}`,
      dayId: day.id,
      nextDayId: next.id,
      dayName: day.name || `Day ${dayIndex + 1}`,
      nextDayName: next.name || `Day ${dayIndex + 2}`,
      scheduledDate: day.scheduledDate || '',
      fromName: last.name || 'Stop',
      toName: firstNext.name || 'Stop',
      fromId: last.id,
      toId: firstNext.id,
      mode: (interDayLegModes || [])[dayIndex] || 'drive',
      kind: 'inter',
      index: dayIndex,
    });
  });
  return legs;
}

export function farmRouteDaySummaries(tourDays, legs) {
  const byDay = new Map();
  (legs || []).forEach((leg) => {
    const list = byDay.get(leg.dayId) || [];
    list.push(leg);
    byDay.set(leg.dayId, list);
  });
  return (tourDays || []).map((day, index) => ({
    id: day.id,
    name: day.name || `Day ${index + 1}`,
    scheduledDate: day.scheduledDate || '',
    stopCount: (day.venues || []).length,
    legs: byDay.get(day.id) || [],
  }));
}

export function collectTourRouteGeometries(tourDays, interDayLegModes, routeCache, filter = null) {
  const geometries = [];
  const cache = routeCache || {};
  const days = Array.isArray(tourDays) ? tourDays : [];
  const fallbackLine = (start, end) => ({
    type: 'LineString',
    coordinates: [
      [Number(start.longitude), Number(start.latitude)],
      [Number(end.longitude), Number(end.latitude)],
    ],
  });
  const pushPair = (start, end, mode, cachePrefix = '') => {
    if (!start || !end) return;
    const slng = Number(start.longitude);
    const slat = Number(start.latitude);
    const elng = Number(end.longitude);
    const elat = Number(end.latitude);
    if (![slng, slat, elng, elat].every(Number.isFinite)) return;
    const key = `${cachePrefix}${start.id}-${end.id}-${mode || 'drive'}`;
    const cached = cache[key]?.geometry;
    geometries.push(cached || fallbackLine(start, end));
  };

  const wantDayId = filter?.dayId || null;
  const wantLegId = filter?.legId && filter.legId !== 'all' ? filter.legId : null;

  days.forEach((day, dayIndex) => {
    const venues = day?.venues || [];
    const legModes = day?.legModes || [];
    for (let i = 0; i < venues.length - 1; i += 1) {
      const legId = `${day.id}:${i}`;
      if (wantDayId && day.id !== wantDayId) continue;
      if (wantLegId && wantLegId !== legId) continue;
      pushPair(venues[i], venues[i + 1], legModes[i] || 'drive');
    }
    const next = days[dayIndex + 1];
    if (!next) return;
    const interId = `inter:${day.id}:${next.id}`;
    const matchesInter = wantLegId
      ? wantLegId === interId
      : (!wantDayId || day.id === wantDayId);
    if (!matchesInter) return;
    const from = venues[venues.length - 1];
    const to = (next.venues || [])[0];
    if (!from || !to) return;
    pushPair(from, to, (interDayLegModes || [])[dayIndex] || 'drive', 'inter-');
  });
  return geometries;
}

async function postOverpassQuery(fetchFn, url, query, timeoutMs) {
  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  const timer = timeoutMs && controller
    ? setTimeout(() => controller.abort(), timeoutMs)
    : null;
  try {
    const res = await fetchFn(url, {
      method: 'POST',
      body: query,
      ...(controller ? { signal: controller.signal } : {}),
    });
    if (!res || !res.ok) throw new Error(`Overpass HTTP ${res?.status || 0}`);
    return res.json();
  } finally {
    if (timer) clearTimeout(timer);
  }
}

export async function fetchFoodHubsFromOverpass(sampledCoords, radiusMiles, {
  includeFarms = true,
  includeFoodBanks = true,
  fetchFn = typeof fetch === 'function' ? fetch.bind(globalThis) : null,
  endpoints = OVERPASS_ENDPOINTS,
  timeoutMs = OVERPASS_TIMEOUT_MS,
} = {}) {
  const query = buildAroundOverpassQuery({
    sampledCoords,
    radiusMiles,
    includeFarms,
    includeFoodBanks,
  });
  if (!query || !fetchFn) return [];
  let lastErr = null;
  for (const url of endpoints) {
    try {
      const json = await postOverpassQuery(fetchFn, url, query, timeoutMs);
      return (Array.isArray(json.elements) ? json.elements : [])
        .map(overpassElementToFoodHub)
        .filter(Boolean);
    } catch (err) {
      lastErr = err;
    }
  }
  throw lastErr || new Error('Overpass failed');
}

// The route line sent to the `searchFoodCorridor` Cloud Function: dense enough
// that the server's corridor filter follows the road, small enough for a
// callable payload.
export function corridorLineForServer(lineCoords) {
  return sampleRouteCoordinates(lineCoords, { minSpacingMiles: 3, maxPoints: 400 });
}

/**
 * Farms, food banks, and community fridges along the route.
 * Tries the Cloud Function (bbox chunks, Firestore cache, three Overpass
 * mirrors) first, then the browser's own Overpass query as a fallback.
 * `callServer(payload)` resolves to the callable's data. Returns
 * { hubs, source: 'server' | 'browser', partial }. Throws when both fail.
 */
export async function searchFoodHubsAlongRoute(lineCoords, radiusMiles, {
  callServer = null,
  fetchFn,
  endpoints,
  timeoutMs,
} = {}) {
  const radius = clampFarmRadiusMiles(radiusMiles);
  let serverErr = null;
  if (typeof callServer === 'function') {
    try {
      const data = await callServer({ line: corridorLineForServer(lineCoords), radiusMiles: radius });
      const elements = Array.isArray(data?.elements) ? data.elements : null;
      if (!elements) throw new Error('searchFoodCorridor returned no elements list');
      return {
        hubs: elements.map(overpassElementToFoodHub).filter(Boolean),
        source: 'server',
        partial: Boolean(data.partial),
      };
    } catch (err) {
      serverErr = err;
    }
  }
  // The server already tried three mirrors with faster bbox queries; the
  // browser's slower `around:` query would only add another long wait.
  // A server timeout means the same: the browser query would time out too.
  const serverCode = String(serverErr?.code || '');
  if (serverCode === 'functions/unavailable' || serverCode === 'functions/deadline-exceeded') {
    const err = new Error(`Food corridor search failed (server: ${serverErr.message})`);
    err.serverError = serverErr;
    throw err;
  }
  try {
    const opts = { includeFarms: true, includeFoodBanks: true };
    if (fetchFn !== undefined) opts.fetchFn = fetchFn;
    if (endpoints !== undefined) opts.endpoints = endpoints;
    if (timeoutMs !== undefined) opts.timeoutMs = timeoutMs;
    const hubs = await fetchFoodHubsFromOverpass(sampleRouteCoordinates(lineCoords), radius, opts);
    return { hubs, source: 'browser', partial: false };
  } catch (err) {
    const combined = new Error(`Food corridor search failed (server: ${serverErr?.message || 'not tried'}; browser: ${err?.message || err})`);
    combined.serverError = serverErr;
    combined.browserError = err;
    throw combined;
  }
}
